/**
 * UploadZone — dropzone + vision-mode toggle + per-file progress.
 *
 * Lifted out of Stage1.tsx as part of the Stage 0 / Stage 1 split.
 * This component owns the upload-side state entirely (visionMode,
 * in-flight progress, per-file timeout / abort) so neither Stage 0
 * nor any future caller needs to thread that plumbing.
 *
 * Behaviour parity with the previous inline implementation:
 *   - Multi-file selection processed one at a time so the
 *     extract + classify pipeline stays sequential per file.
 *   - Per-file 180 s hard timeout via AbortController; surfaces as
 *     "timed out — server still processing" in the failure list.
 *   - Vision mode persisted in localStorage under stage1.visionMode
 *     so a drafter's preference doesn't reset across navigation.
 *   - Progress banner colour reflects outcome (blue in flight,
 *     green on success, amber on partial failure, red on total
 *     failure).
 *
 * What's intentionally NOT here:
 *   - Optimistic file rows in the Uploaded Files list. The previous
 *     implementation kept those local to Stage 1 so files appeared
 *     instantly on drop. That behaviour now relies on
 *     UploadedFilesList's refreshKey mechanism — the parent bumps a
 *     counter when onUploaded fires and the list refetches at once.
 *     One poll cycle later (≤5 s) the row is visible. Given the
 *     UploadZone progress banner is ALSO visible during the upload,
 *     the lost optimistic-row latency is informational only.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Upload, ImageIcon, Loader2, AlertTriangle, CheckCircle, X,
} from 'lucide-react';
import * as api from '@/api/client';


export type VisionMode = 'auto' | 'on' | 'off';

/** What each Vision setting does (server.py upload_idf + the
 *  vision_analysis_on_upload system flag, default off, which forces
 *  'off' whatever is picked here). */
export const VISION_MODE_HINTS: Record<VisionMode, string> = {
  off: 'Off: read the text only; pictures inside files are not described by the AI. Fastest.',
  auto: 'Auto: have the AI describe pictures inside files (figures in Word, scanned PDF pages) '
    + 'when an AI model is configured; otherwise skip.',
  on: 'On: always try to have the AI describe pictures inside files, and show a warning if it fails.',
};
/** What happens after an upload (jobs/document_jobs.py: extract →
 *  search index → classify run automatically; the Stage 1 analysis
 *  only re-reads the documents on Analyze IDF / Rescore / Re-extract). */
export const UPLOAD_NEXT_STEP_HINT =
  'Each file is read, indexed and classified automatically. Stage 1 does not re-analyse '
  + 'by itself: once the file shows as indexed, click Analyze IDF (first time) or Rescore '
  + 'in Stage 1 to bring it into the summary, scores and questions.';

export const VISION_ADMIN_NOTE =
  'An admin setting (Run vision analysis during IDF upload, off by default) can switch this '
  + 'off for every upload; figures can still be described one by one from the Stage 1 '
  + 'Figures tab (Re-analyse).';


interface UploadZoneProps {
  matterId: string;
  /** Disables the dropzone — used when Stage 1 is frozen and the
   *  drafter must Rescore before new docs are picked up by analysis. */
  disabled?: boolean;
  /** Optional copy override for the disabled state. Defaults to a
   *  stage-neutral "Upload disabled" so callers can tailor the
   *  message (e.g. "Stage 1 is frozen — Rescore to incorporate"). */
  disabledLabel?: string;
  /** Fired after EACH successful file upload + the per-file refetch
   *  hand-off. Parents use this to bump UploadedFilesList's
   *  refreshKey so the new row appears without waiting for the
   *  poller tick, and to refresh the matter blob (completeness
   *  scores / idf_documents). Best-effort — exceptions thrown by
   *  the callback don't abort the remaining files in the batch. */
  onUploaded?: () => void;
  /** Fired when an upload returns 202 (async path) so the parent
   *  can record the (document_id → job_id) mapping. UploadedFilesList
   *  reads that map to drive per-row step-progress polling against
   *  /api/jobs/{job_id}/status. The mapping is in-memory only — a
   *  page reload mid-upload loses it; the next listMatterDocuments
   *  poll picks the row up via its status alone (UploadedFilesList
   *  shows a generic "Processing…" chip without the step detail
   *  until the next 202 response re-populates the map). */
  onJobIdMapped?: (documentId: string, jobId: string) => void;
  /** Fired the moment a file enters the upload loop — BEFORE the
   *  POST is dispatched. Drives the optimistic placeholder rows in
   *  UploadedFilesList so the drafter sees the file land in the
   *  list immediately rather than waiting for the backend round-
   *  trip + the next poll tick (0–5s window). The tempId is a
   *  client-side UUID; the parent uses it to settle / reconcile
   *  the placeholder via onUploadSettled below. */
  onUploadStart?: (entry: {
    tempId: string;
    filename: string;
    size: number;
  }) => void;
  /** Fired when a per-file upload settles — success (200/202) OR
   *  failure (network error, timeout, 4xx/5xx). The parent uses
   *  this to remove the placeholder; on success the real row
   *  appears via the next list refetch, on failure the parent
   *  may surface a toast. */
  onUploadSettled?: (
    tempId: string,
    outcome: { ok: boolean; filename: string; error?: string },
  ) => void;
}


/** Per-file hard timeout. Inline extraction caps at ~50s server-side
 *  (20s text + 30s vision) but a hung proxy can leave POST waiting
 *  forever and axios has no default timeout. 180s is the comfortable
 *  buffer; aborts surface as a typed failure in the progress banner. */
const PER_FILE_TIMEOUT_MS = 180_000;


function readPersistedVisionMode(): VisionMode {
  try {
    const v = localStorage.getItem('stage1.visionMode');
    if (v === 'auto' || v === 'on' || v === 'off') return v;
  } catch { /* localStorage unavailable */ }
  return 'off';
}


function stringifyUploadError(e: any): string {
  // 413 comes from the reverse proxy (nginx), whose body is HTML —
  // there is no JSON detail to surface, so without this branch the
  // drafter saw the raw axios "Request failed with status code 413".
  if (e?.response?.status === 413) {
    return (
      'File rejected as too large (HTTP 413). The app accepts up to ' +
      '50 MB per file. If this file is under 50 MB, the deployment’s ' +
      'reverse proxy is capping uploads below that — ask an admin to ' +
      'set nginx client_max_body_size to 50M for this domain and reload.'
    );
  }
  // The 409 "IDF already uploaded" payload is an OBJECT
  // (detail = {needs_confirmation, reason, message, existing_idfs}),
  // so plain `detail || message` rendered "[object Object]". Pull
  // .message when present, JSON-encode if nothing else.
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }
  return e?.message || 'Upload failed';
}


/**
 * Step → label mapping for the per-job async progress banner.
 * Steps come straight from document_jobs.update_progress() on the
 * worker side. ``queued`` is synthesised here when the poll sees a
 * non-null ``queue_position`` so the queue-position case feels
 * symmetrical with the worker-driven steps.
 */
const ASYNC_STEP_LABELS: Record<string, string> = {
  queued:     '⏳ Waiting in queue…',
  extracting: '⏳ Extracting text…',
  figures:    '⏳ Processing figures…',
  indexing:   '⏳ Building search index…',
  vision:     '⏳ Running vision analysis…',
  complete:   '✅ Ready',
  failed:     '⚠ Failed',
};

const ASYNC_POLL_MS   = 3_000;        // 3s cadence per spec
const ASYNC_POLL_MAX  = 100;          // 100 * 3s = 5 min ceiling


/** Per-file async-job state — one entry per 202-receiving upload. */
interface AsyncJobRow {
  filename: string;
  documentId: string;
  jobId: string;
  step: string;             // 'queued' | 'extracting' | … | 'complete' | 'failed'
  message: string;
  finished: boolean;        // settled (complete or failed); kept for the brief
                            // post-completion display before the row auto-dismisses
}


export default function UploadZone({
  matterId,
  disabled = false,
  disabledLabel = 'Upload disabled',
  onUploaded,
  onJobIdMapped,
  onUploadStart,
  onUploadSettled,
}: UploadZoneProps) {
  const [uploading, setUploading] = useState(false);
  const [visionMode, setVisionMode] = useState<VisionMode>(readPersistedVisionMode);
  const [uploadProgress, setUploadProgress] = useState<{
    done: number; total: number; current: string;
    warnings: string[];
    failures: string[];
  } | null>(null);

  // In-flight + recently-settled async jobs. Renders as one row per
  // job inside the progress banner so a multi-file async upload
  // shows each file's pipeline stage independently. Settled rows
  // auto-dismiss after ~6s so the panel doesn't stay cluttered.
  const [asyncJobs, setAsyncJobs] = useState<AsyncJobRow[]>([]);

  // Persist vision mode under the same localStorage key the previous
  // inline implementation used so existing drafter preferences carry
  // across the move.
  useEffect(() => {
    try { localStorage.setItem('stage1.visionMode', visionMode); } catch {}
  }, [visionMode]);

  // In-flight batch counter so concurrent drag-drops don't race the
  // uploading flag back to false while the second batch is still
  // running. Each handleUpload invocation manages its own files.
  const inFlightRef = useRef<number>(0);

  // Track active poll timers so an unmount mid-poll cleans them up.
  // Keyed by jobId; values are the interval handles.
  const pollersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    return () => {
      // Clean up every poll timer on unmount.
      pollersRef.current.forEach((handle) => window.clearInterval(handle));
      pollersRef.current.clear();
    };
  }, []);

  /**
   * startPolling — RQ job-status poller for a single async upload.
   * Mirrors the spec's ``startPolling`` shape: 3s cadence, 5-min
   * ceiling, callbacks fire on progress / complete / failed. Updates
   * the matching row in ``asyncJobs`` on every tick so the banner's
   * step label tracks the worker.
   */
  const startPolling = (jobId: string) => {
    let attempts = 0;
    const handle = window.setInterval(async () => {
      attempts += 1;
      try {
        const data = await api.getJobStatus(jobId);

        // Step + message resolution priority:
        //   1. progress beacon written by document_jobs.update_progress
        //   2. queue_position synthesised → step='queued'
        //   3. raw RQ status (queued / started / finished / failed)
        let step = (data.progress?.step) || data.status || 'started';
        let message = (data.progress?.message)
          || ASYNC_STEP_LABELS[step]
          || `Status: ${data.status}`;
        if (data.queue_position && data.queue_position > 0) {
          step = 'queued';
          message = `Position ${data.queue_position} in queue…`;
        }

        if (data.status === 'finished') {
          setAsyncJobs((prev) => prev.map((j) => j.jobId === jobId
            ? { ...j, step: 'complete', message: ASYNC_STEP_LABELS.complete, finished: true }
            : j
          ));
          window.clearInterval(handle);
          pollersRef.current.delete(jobId);
          if (onUploaded) {
            try { onUploaded(); } catch { /* best-effort */ }
          }
          // Auto-dismiss the row after a brief hold so the drafter
          // sees the success state before it disappears.
          window.setTimeout(() => {
            setAsyncJobs((prev) => prev.filter((j) => j.jobId !== jobId));
          }, 6_000);
          return;
        }
        if (data.status === 'failed') {
          const errMsg = data.error || 'Job failed';
          setAsyncJobs((prev) => prev.map((j) => j.jobId === jobId
            ? { ...j, step: 'failed', message: `⚠ ${errMsg.slice(0, 200)}`, finished: true }
            : j
          ));
          window.clearInterval(handle);
          pollersRef.current.delete(jobId);
          // Failed rows hang around longer so the drafter can read
          // the error message before it's auto-dismissed.
          window.setTimeout(() => {
            setAsyncJobs((prev) => prev.filter((j) => j.jobId !== jobId));
          }, 15_000);
          return;
        }

        setAsyncJobs((prev) => prev.map((j) => j.jobId === jobId
          ? { ...j, step, message }
          : j
        ));

        if (attempts >= ASYNC_POLL_MAX) {
          setAsyncJobs((prev) => prev.map((j) => j.jobId === jobId
            ? { ...j, step: 'failed', message: '⚠ Polling timed out (still processing on server)', finished: true }
            : j
          ));
          window.clearInterval(handle);
          pollersRef.current.delete(jobId);
        }
      } catch (e: any) {
        // Network error mid-poll — keep retrying until MAX. Don't
        // surface intermediate failures; transient blips shouldn't
        // panic the drafter.
        if (attempts >= ASYNC_POLL_MAX) {
          setAsyncJobs((prev) => prev.map((j) => j.jobId === jobId
            ? { ...j, step: 'failed', message: `⚠ ${e?.message || 'Polling failed'}`, finished: true }
            : j
          ));
          window.clearInterval(handle);
          pollersRef.current.delete(jobId);
        }
      }
    }, ASYNC_POLL_MS);
    pollersRef.current.set(jobId, handle);
  };

  const handleUpload = async (
    filesIn: FileList | File[],
  ) => {
    if (disabled || filesIn.length === 0) return;
    // Snapshot the input into a stable Array<File> BEFORE the first
    // await yields. The legacy code accepted a FileList here and
    // indexed into it inside the loop — but the <input>'s onChange
    // handler clears `e.target.value` immediately after calling us
    // (so drafters can re-select the same file), and the FileList
    // is a LIVE reference to the input's current selection. The
    // first iteration of the loop ran fine because it captured
    // files[0] synchronously before yielding; iteration 2+ then
    // read files[1] / files[2] from a now-empty FileList and silently
    // skipped the rest of the batch. Array.from() materialises a
    // detached copy that survives the value clear.
    const files: File[] = Array.from(filesIn);
    inFlightRef.current += 1;
    setUploading(true);
    const total = files.length;
    const warnings: string[] = [];
    const failures: string[] = [];

    for (let i = 0; i < total; i++) {
      const f = files[i];
      if (!f) {
        // Defence-in-depth: should be unreachable given the
        // Array.from snapshot above, but a stray undefined would
        // otherwise crash api.uploadIdf with a confusing
        // FormData.append type error.
        failures.push(`(file ${i + 1}): missing — skipped`);
        continue;
      }
      setUploadProgress({ done: i, total, current: f.name, warnings, failures });
      // OPT-1 — emit a placeholder-row event *before* the POST
      // dispatches so the file appears in UploadedFilesList in
      // the same tick the drafter dropped it. tempId reconciles
      // the success / failure callback with the placeholder.
      const tempId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`;
      if (onUploadStart) {
        try { onUploadStart({ tempId, filename: f.name, size: f.size }); }
        catch { /* parent-callback errors are best-effort */ }
      }
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), PER_FILE_TIMEOUT_MS);
      let fileOk = false;
      let settledError: string | undefined;
      let fireOnUploaded = true;  // sync 200 → fire immediately;
                                  // async 202 → defer until the poll completes
      try {
        // doc_type='auto' tells the backend to defer classification to
        // the post-extraction lite-tier classifier in
        // backend/jobs/document_jobs._classify_document. The drafter
        // promotes / re-classifies after the fact via the per-row
        // dropdown in UploadedFilesList — there is no upload-time
        // classification step.
        const result = await api.uploadIdf(
          matterId, f, visionMode, 'auto',
          false, controller.signal,
        );
        if (result.status === 200) {
          // ════ Existing sync path — works as before ════
          const data = result.data;
          if (Array.isArray(data?.extraction_warnings)) {
            for (const w of data.extraction_warnings) warnings.push(`${f.name}: ${w}`);
          }
          fileOk = true;
        } else if (result.status === 202) {
          // ════ New async path ════
          const { document_id, job_id } = result.data;
          // Seed an in-flight row immediately so the drafter sees
          // the upload register before the first poll lands.
          setAsyncJobs((prev) => [
            ...prev,
            {
              filename: f.name,
              documentId: document_id,
              jobId: job_id,
              step: 'queued',
              message: ASYNC_STEP_LABELS.queued,
              finished: false,
            },
          ]);
          // Tell the parent about the mapping so UploadedFilesList
          // (which poll-renders the step list per row) can pick it
          // up. Best-effort — a crashed callback shouldn't kill
          // the remainder of the batch.
          if (onJobIdMapped) {
            try { onJobIdMapped(document_id, job_id); } catch { /* tolerate */ }
          }
          startPolling(job_id);
          // The list-refresh callback fires from the poll's
          // onComplete branch, NOT here — otherwise UploadedFilesList
          // would refetch + show a row with status='uploading'
          // that's already half-rendered by its own 5s poll.
          fireOnUploaded = false;
          fileOk = true;
        }
      } catch (e: any) {
        const aborted = e?.code === 'ERR_CANCELED'
          || e?.name === 'CanceledError'
          || e?.name === 'AbortError';
        const reason = aborted
          ? `timed out after ${PER_FILE_TIMEOUT_MS / 1000}s — server still processing`
          : stringifyUploadError(e);
        failures.push(`${f.name}: ${reason}`);
        settledError = reason;
      } finally {
        window.clearTimeout(timer);
      }
      setUploadProgress({ done: i + 1, total, current: f.name, warnings, failures });

      // OPT-1 — settle the placeholder. Fires for both success
      // (so the placeholder makes way for the real row landing
      // via the next list refetch) and failure (so the parent
      // can surface an inline toast).
      if (onUploadSettled) {
        try {
          onUploadSettled(tempId, {
            ok: fileOk,
            filename: f.name,
            error: settledError,
          });
        } catch { /* tolerate */ }
      }

      if (fileOk && fireOnUploaded && onUploaded) {
        try { onUploaded(); } catch { /* parent-callback errors are best-effort */ }
      }
    }

    inFlightRef.current = Math.max(0, inFlightRef.current - 1);
    if (inFlightRef.current === 0) setUploading(false);

    // Auto-dismiss the progress banner. Failures stay up longer so
    // the drafter has time to read them; warnings get a medium hold;
    // clean runs disappear quickly so the panel doesn't stay cluttered.
    const dismissDelayMs = failures.length > 0 ? 12000 : warnings.length > 0 ? 8000 : 1500;
    setTimeout(() => setUploadProgress(null), dismissDelayMs);
  };

  return (
    <div data-testid="upload-zone" className="border border-zinc-200 rounded-sm bg-white p-6">
      <label
        className={`flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
          disabled
            ? 'border-zinc-200 bg-zinc-50 cursor-not-allowed'
            : 'border-zinc-300 hover:border-[#002FA7] hover:bg-blue-50/30'
        }`}
      >
        <Upload size={32} className="text-zinc-400" />
        <span className="text-sm text-zinc-600">
          {disabled ? disabledLabel : 'Drop files or click to upload'}
        </span>
        <span className="text-xs text-zinc-400">
          Word · PDF · PowerPoint · Excel · Markdown · HTML · RTF · CSV · TSV · JSON · XML · Email · Image · Text
        </span>
        <span className="text-[10px] text-zinc-400">
          Multiple files are processed one at a time. Max 50 MB per file.
        </span>
        <input
          data-testid="upload-idf-input"
          type="file"
          className="hidden"
          accept=".docx,.doc,.pdf,.pptx,.ppt,.txt,.xlsx,.xlsm,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.md,.html,.htm,.rtf,.csv,.tsv,.json,.xml,.eml,message/rfc822,text/markdown,text/html,text/csv,text/tab-separated-values,application/json,application/xml,text/xml,application/rtf"
          multiple
          disabled={disabled}
          onChange={(e) => {
            // Snapshot the live FileList into a detached array
            // BEFORE clearing the input value. ``e.target.files``
            // is a live reference — clearing ``e.target.value``
            // empties the same FileList, so any read after the
            // clear sees length=0. Materialising via Array.from
            // first keeps the file references alive past the
            // value reset; the reset itself is what lets the
            // drafter re-pick the same file after a transient
            // failure.
            const files = e.target.files
              ? Array.from(e.target.files)
              : [];
            e.target.value = '';
            if (files.length === 0) return;
            handleUpload(files);
          }}
        />
      </label>

      {!disabled && (
        <div className="mt-3 flex items-center justify-between text-xs">
          <div
            className="flex items-center gap-1.5 text-zinc-500 cursor-help"
            data-testid="vision-mode-help"
            title={[VISION_MODE_HINTS.off, VISION_MODE_HINTS.auto, VISION_MODE_HINTS.on, VISION_ADMIN_NOTE].join('\n')}
          >
            <ImageIcon size={12} />
            <span>Vision for images in uploads:</span>
          </div>
          <div className="flex items-center gap-1" data-testid="vision-mode-toggle">
            {(['off', 'auto', 'on'] as const).map(mode => (
              <button
                key={mode}
                type="button"
                data-testid={`vision-mode-${mode}`}
                title={VISION_MODE_HINTS[mode]}
                aria-pressed={visionMode === mode}
                onClick={() => setVisionMode(mode)}
                disabled={uploading}
                className={`px-2 py-0.5 rounded-sm border text-[11px] transition-colors ${
                  visionMode === mode
                    ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>
      )}
      {!disabled && (
        <p className="mt-1 text-[11px] text-zinc-400" data-testid="vision-mode-hint">
          {VISION_MODE_HINTS[visionMode]}
          {visionMode !== 'off' && ` ${VISION_ADMIN_NOTE}`}
        </p>
      )}

      {uploadProgress && (() => {
        const inFlight = uploadProgress.done < uploadProgress.total;
        const failedCount = uploadProgress.failures.length;
        const succeeded = uploadProgress.total - failedCount;
        const allFailed = !inFlight && failedCount === uploadProgress.total;
        const anyFailed = !inFlight && failedCount > 0;
        const containerClass = inFlight
          ? 'bg-blue-50/60 border-blue-200 text-[#002FA7]'
          : allFailed
            ? 'bg-red-50 border-red-200 text-red-800'
            : anyFailed
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-blue-50/60 border-blue-200 text-[#002FA7]';
        return (
          <div
            data-testid="upload-progress"
            className={`mt-3 p-2 border rounded-sm text-xs flex items-start gap-2 ${containerClass}`}
          >
            {inFlight ? (
              <Loader2 size={14} className="animate-spin flex-shrink-0 mt-0.5" />
            ) : allFailed ? (
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-red-600" />
            ) : anyFailed ? (
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
            ) : (
              <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-green-600" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium">
                {inFlight
                  ? `Uploading ${uploadProgress.done + 1} of ${uploadProgress.total} — ${uploadProgress.current}`
                  : anyFailed
                    ? `${succeeded} of ${uploadProgress.total} uploaded · ${failedCount} failed`
                    : `Uploaded ${uploadProgress.total} file${uploadProgress.total === 1 ? '' : 's'} — processing may continue in background`}
              </div>
              {!inFlight && succeeded > 0 && (
                <div className="mt-0.5 text-[11px] opacity-80" data-testid="upload-next-step-hint">
                  {UPLOAD_NEXT_STEP_HINT}
                </div>
              )}
              {uploadProgress.warnings.length > 0 && (
                <ul className="mt-1 text-[11px] text-amber-800 space-y-0.5">
                  {uploadProgress.warnings.slice(0, 4).map((w, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <AlertTriangle size={10} className="flex-shrink-0 mt-0.5 text-amber-500" />
                      <span>{w}</span>
                    </li>
                  ))}
                  {uploadProgress.warnings.length > 4 && (
                    <li className="text-zinc-500">… and {uploadProgress.warnings.length - 4} more</li>
                  )}
                </ul>
              )}
              {uploadProgress.failures.length > 0 && (
                <ul
                  data-testid="upload-progress-failures"
                  className="mt-1 text-[11px] text-red-800 space-y-0.5"
                >
                  {uploadProgress.failures.slice(0, 4).map((f, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <X size={10} className="flex-shrink-0 mt-0.5 text-red-600" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {uploadProgress.failures.length > 4 && (
                    <li className="text-zinc-500">… and {uploadProgress.failures.length - 4} more</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        );
      })()}

      {/* Async-job rows — one per 202-receiving upload while it's
          processing on the worker, plus a brief post-completion
          hold. The legacy uploadProgress banner above covers the
          synchronous / HTTP-roundtrip half; this section covers
          the worker-pipeline half (extracting → indexing → vision
          → complete). When the use_async_upload flag is off this
          array is always empty and the section renders nothing. */}
      {asyncJobs.length > 0 && (
        <div data-testid="upload-async-jobs" className="mt-3 space-y-1.5">
          {asyncJobs.map((j) => {
            const failed = j.step === 'failed';
            const done   = j.step === 'complete';
            const cls = done
              ? 'bg-green-50 border-green-200 text-green-900'
              : failed
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50/60 border-blue-200 text-[#002FA7]';
            return (
              <div
                key={j.jobId}
                data-testid={`upload-async-job-${j.jobId}`}
                data-step={j.step}
                className={`p-2 border rounded-sm text-xs flex items-start gap-2 ${cls}`}
              >
                {done ? (
                  <CheckCircle size={14} className="flex-shrink-0 mt-0.5 text-green-600" />
                ) : failed ? (
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-red-600" />
                ) : (
                  <Loader2 size={14} className="animate-spin flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {j.filename}
                  </div>
                  <div className="text-[11px] opacity-80">{j.message}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
